import React, { useState, useCallback } from "react";
import { IoClose } from 'react-icons/io5';
import { BsPlusCircle } from "react-icons/bs";
import { FaTrash } from 'react-icons/fa';
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';

import { registerCategoria } from "../../services/api";
import { getStoredUser } from '../../utils/safeStorage';
import '../../styles/modal-shared.css';

const Days = [{ domingo: "D", segunda: "S", terca: "T", quarta: "Q", quinta: "Q", sexta: "S", sabado: "S" }];

const ModalAddCategoria = ({ showModal, setShowModal, setRefresh }) => {
    const { t } = useTranslation('categorias');
    const userParse = getStoredUser();
    const userID = userParse?._id;

    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;

    const options = [
        { value: "sempre", label: t('availability.always') },
        { value: "pausado", label: t('availability.paused') },
        { value: "especificos", label: t('availability.scheduled') }
    ];

    const optionsModelo = [
        { value: 1, label: t('models.mainItems') },
        { value: 2, label: t('models.pizza') }
    ];

    const [selectedOption, setSelectedOption] = useState("");
    const [diasHorarios, setDiasHorarios] = useState([
        { dias: new Set(), startTime: "00:00", endTime: "23:59" }
    ]);

    // Helper: clonar array de horarios com deep copy dos Sets
    const cloneHorarios = useCallback((horarios) => {
        return horarios.map(h => ({
            dias: new Set(h.dias),
            startTime: h.startTime,
            endTime: h.endTime
        }));
    }, []);

    const SignUpSchema = Yup.object().shape({
        modelo: Yup.mixed().required(t('actions.required')),
        title: Yup.string().required(t('actions.required')),
    });

    const formik = useFormik({
        validationSchema: SignUpSchema,
        validateOnBlur: false,
        validateOnChange: false,
        initialValues: {
            modelo: optionsModelo[0],
            title: "",
        },
        onSubmit: (values) => {
            const title = values.title;
            handleSubmitAPI(title);
        },
    });

    const handleClose = () => {
        setShowModal(false);
        formik.resetForm();
        setSelectedOption("");
        setDiasHorarios([{ dias: new Set(), startTime: "00:00", endTime: "23:59" }]);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) handleClose();
    };

    const handleSubmitAPI = (title) => {
        const createdBy = userID;
        const only_pdv = false;
        const only_qrcode = false;
        const only_delivery_take_local = false;
        let disponibilidade = selectedOption || "sempre";
        const modelo = formik.values.modelo;

        // Converter formato agrupado (Set de dias) para formato flat (1 objeto por dia)
        const horariosFlat = [];
        diasHorarios.forEach(({ dias, startTime, endTime }) => {
            dias.forEach(dia => {
                if (dia && dia !== "") {
                    horariosFlat.push({ dia, startTime, endTime });
                }
            });
        });

        registerCategoria(createdBy, idEmpresa, title, disponibilidade, horariosFlat, only_pdv, only_qrcode, only_delivery_take_local, modelo).then(cast => {
            if (cast.status === 201) {
                toast(cast.data.msg, { autoClose: 5000, type: "success" });
                setRefresh(true);
                handleClose();
            } else {
                toast(cast.data.msg, { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            const msg = err.response?.data?.msg || t('actions.deleteCategoryError');
            toast(msg, { autoClose: 5000, type: "error" });
        });
    };

    const handleDeleteHorario = useCallback((horarioIndex) => {
        setDiasHorarios(prev => {
            if (prev.length <= 1) {
                // Manter pelo menos 1 bloco - resetar ao invés de deletar
                return [{ dias: new Set(), startTime: "00:00", endTime: "23:59" }];
            }
            const next = [...prev];
            next.splice(horarioIndex, 1);
            return next;
        });
    }, []);

    const handleOptionChange = (event) => {
        const newValue = event.target.value;
        if (newValue === "sempre" || newValue === "pausado") {
            setDiasHorarios([{ dias: new Set(), startTime: "00:00", endTime: "23:59" }]);
        }
        setSelectedOption(newValue);
    };

    const handleDayChange = useCallback((horarioIndex, key) => {
        setDiasHorarios(prev => {
            const newHorarios = cloneHorarios(prev);
            const horario = newHorarios[horarioIndex];
            if (!horario) return prev;

            if (horario.dias.has(key)) {
                horario.dias.delete(key);
                horario.dias.delete("");
            } else {
                horario.dias.add(key);
                horario.dias.delete("");
            }
            return newHorarios;
        });
    }, [cloneHorarios]);

    const handleTimeChange = useCallback((horarioIndex, field, event) => {
        const value = event.target.value;
        setDiasHorarios(prev => {
            const newHorarios = cloneHorarios(prev);
            if (newHorarios[horarioIndex]) {
                newHorarios[horarioIndex][field] = value;
            }
            return newHorarios;
        });
    }, [cloneHorarios]);

    const handleAddHorario = useCallback(() => {
        setDiasHorarios(prev => [
            ...prev,
            { dias: new Set(), startTime: "00:00", endTime: "23:59" }
        ]);
    }, []);

    return (
        <div
            className={showModal ? 'pdj-modal-overlay' : 'pdj-modal-overlay--hidden'}
            onClick={handleBackdropClick}
        >
            <div className="pdj-modal-box pdj-modal-box--narrow">
                <form onSubmit={formik.handleSubmit}>

                    {/* Header */}
                    <div className="pdj-modal-header">
                        <div className="pdj-modal-header-text">
                            <h2 className="pdj-modal-title">{t('addCategoryModal.title')}</h2>
                        </div>
                        <button type="button" className="pdj-modal-close-btn" onClick={handleClose}>
                            <IoClose />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="pdj-modal-body">

                        {/* Modelo */}
                        <div className="pdj-modal-field">
                            <label className="pdj-modal-label">
                                {t('models.label')}
                                <span className="pdj-modal-required">*</span>
                            </label>
                            <select
                                className="pdj-modal-select"
                                id="modelo"
                                name="modelo"
                                value={formik.values.modelo ? formik.values.modelo.value : ''}
                                onChange={(e) => {
                                    const opt = optionsModelo.find(o => o.value === parseInt(e.target.value));
                                    formik.setFieldValue("modelo", opt);
                                }}
                            >
                                {optionsModelo.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {formik.errors.modelo && (
                                <span className="pdj-modal-error">{formik.errors.modelo}</span>
                            )}
                        </div>

                        {/* Nome da Categoria */}
                        <div className="pdj-modal-field">
                            <label className="pdj-modal-label">
                                {t('addCategoryModal.categoryName')}
                                <span className="pdj-modal-required">*</span>
                            </label>
                            <input
                                className="pdj-modal-input"
                                placeholder={t('addCategoryModal.categoryNamePlaceholder')}
                                id="title"
                                type="text"
                                name="title"
                                onChange={formik.handleChange}
                                value={formik.values.title}
                            />
                            {formik.errors.title && (
                                <span className="pdj-modal-error">{formik.errors.title}</span>
                            )}
                        </div>

                        <hr className="pdj-modal-divider" />

                        {/* Disponibilidade */}
                        <div className="pdj-modal-field">
                            <label className="pdj-modal-label">
                                {t('availability.label')}
                                <span className="pdj-modal-required">*</span>
                            </label>
                            <select
                                className="pdj-modal-select"
                                value={selectedOption}
                                onChange={handleOptionChange}
                            >
                                {options.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Horarios especificos */}
                        {selectedOption === "especificos" && (
                            <>
                                {diasHorarios.map((horario, horarioIndex) => (
                                    <div key={horarioIndex} className="pdj-modal-schedule-block">

                                        {/* Header do bloco */}
                                        <div className="pdj-modal-schedule-block-header">
                                            <span className="pdj-modal-schedule-block-number">
                                                {t('schedule.scheduleNumber', { number: horarioIndex + 1, defaultValue: `Horario ${horarioIndex + 1}` })}
                                            </span>
                                            <button
                                                type="button"
                                                className="pdj-modal-btn pdj-modal-btn--danger"
                                                onClick={() => handleDeleteHorario(horarioIndex)}
                                                style={{ padding: '0 8px', height: '28px', fontSize: '11px' }}
                                            >
                                                <FaTrash size={10} />
                                            </button>
                                        </div>

                                        {/* Dias da semana */}
                                        <div className="pdj-modal-field" style={{ gap: '4px' }}>
                                            <label className="pdj-modal-label" style={{ fontSize: '12px', color: '#64748b' }}>
                                                {t('schedule.availableDays')}
                                            </label>
                                            <div className="pdj-modal-day-chips">
                                                {Days[0] && Object.entries(Days[0]).map(([key, value]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        className={`pdj-modal-day-chip${horario.dias.has(key) ? ' pdj-modal-day-chip--active' : ''}`}
                                                        onClick={() => handleDayChange(horarioIndex, key)}
                                                    >
                                                        {t(`schedule.days.${key}`, value)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Horarios */}
                                        <div className="pdj-modal-schedule-item">
                                            <input
                                                type="time"
                                                className="pdj-modal-input"
                                                value={horario.startTime}
                                                onChange={(event) => handleTimeChange(horarioIndex, "startTime", event)}
                                            />
                                            <span className="pdj-modal-schedule-separator">{t('schedule.to')}</span>
                                            <input
                                                type="time"
                                                className="pdj-modal-input"
                                                value={horario.endTime}
                                                onChange={(event) => handleTimeChange(horarioIndex, "endTime", event)}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className="pdj-modal-btn pdj-modal-btn--secondary"
                                    onClick={handleAddHorario}
                                    style={{ alignSelf: 'flex-start' }}
                                >
                                    <BsPlusCircle />
                                    {t('schedule.addTime')}
                                </button>
                            </>
                        )}

                    </div>

                    {/* Footer */}
                    <div className="pdj-modal-footer">
                        <button type="button" className="pdj-modal-btn pdj-modal-btn--secondary" onClick={handleClose}>
                            {t('actions.cancel')}
                        </button>
                        <button type="submit" className="pdj-modal-btn pdj-modal-btn--primary">
                            {t('actions.save')}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default ModalAddCategoria;
